import {
  type CSSProperties,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import 'suneditor/src/assets/css/suneditor-contents.css';
import {
  NeonButton,
  NeonButtonFooter,
  NeonCardStandard,
  NeonErrorPageContent,
  NeonIcon,
  NeonLoadingIndicator,
  NeonModalDialog,
  NeonTag,
} from '@ps-refarch-ux/neon';
import { DATA_SLOT, NeonButtonTypes } from '@/constants/neon-constants';
import {
  ADD_COURSE_PAGE_TEXT,
  COURSE_CATALOG_ID,
  DELETE_COURSE_TOAST,
  formatCourseDeletedSuccessHeading,
  getCourseDurationDisplayLabel,
  getViewCourseBreadcrumbs,
  VIEW_COURSE_PAGE_TEXT,
} from '@/constants/course-catalog-constants';
import { STAFF_ROUTES } from '@/constants/staff-routes';
import { FormPageShell } from '@/components/FormPageShell/form-page-shell';
import { PageTitle } from '@/components/PageTitle/page-title';
import formLayoutStyles from '@/components/MultiSectionFormLayout/multi-section-form-layout.module.scss';
import { usePageFooter } from '@/context';
import { useGetCourse, useDeleteCourse } from '@/repositories/course-repository';
import { useGetSchools } from '@/repositories/schools-repository';
import { useGetStudentSchools } from '@/repositories/student/school-repository';
import { useToastNotifications } from '@/hooks/use-toast-notifications';
import type { Course, CourseSummary } from '@/types';
import { CourseStatus } from '@/types';
import { isCourseCatalogReadOnly } from '@/utils/cp-context';
import { DeleteCourseModal } from './delete-course-modal';
import { CourseSchoolsMappingDrawer } from './course-schools-mapping-drawer';
import detailPageStyles from '@/components/DetailPageUi/detail-page-ui.module.scss';
import { sanitizeRichHtmlForDisplay } from '@/utils/sanitize-rich-html';
import { bottomBarStyle } from '@/features/student/add-area/styles';
import styles from './view-course-page.module.scss';

const MAX_RELATIONSHIP_TILE_ROWS = 2;
/** District catalog import stores a placeholder school NID (`{digits}DUS`) when a course has no school mapping; omit from the course details list (NAVI-42250). */
const DUS_NID_PATTERN = /^\d+DUS$/i;

/** API HTML `description` only; matches Plan of Study detail rendering. */
function renderCourseDescriptionContent(description: string | undefined): ReactElement {
  const html = description?.trim();
  if (html) {
    const safeHtml = sanitizeRichHtmlForDisplay(html);
    return (
      <div className={detailPageStyles.descriptionRichScroll}>
        <div className="sun-editor-editable" dangerouslySetInnerHTML={{ __html: safeHtml }} />
      </div>
    );
  }
  return (
    <p>
      <span className={detailPageStyles.noDescription}>No description provided</span>
    </p>
  );
}

function toRelationshipGroups(
  input?: Array<string[] | Course[] | CourseSummary[] | { id: string; name?: string }[]>
): Array<Array<{ id: string; name: string }>> {
  if (!input?.length) {
    return [];
  }
  return input
    .map((group) => {
      if (!Array.isArray(group)) {
        return [];
      }
      return group
        .map((item) => {
          if (typeof item === 'string') {
            return item ? { id: item, name: item } : null;
          }
          const id = typeof item?.id === 'string' ? item.id : '';
          if (!id) {
            return null;
          }
          const name =
            typeof item?.name === 'string' && item.name.trim().length > 0 ? item.name : id;
          return { id, name };
        })
        .filter((x): x is { id: string; name: string } => x !== null);
    })
    .filter((g) => g.length > 0);
}

function ViewCourseStickyFooter({
  isDistrictLevel,
  isCourseLoading,
  onPrevious,
  onEdit,
  onDelete,
}: {
  isDistrictLevel: boolean;
  isCourseLoading: boolean;
  onPrevious: () => void;
  onEdit: () => void;
  onDelete: () => void;
}): ReactElement | null {
  const { setFooterNode } = usePageFooter();

  useEffect(() => {
    if (isDistrictLevel) {
      setFooterNode(
        <NeonCardStandard
          className={formLayoutStyles.formActionsCard}
          dataInjectionSpacing="false"
          style={{ borderRadius: 0 }}
        >
          <div data-slot={DATA_SLOT} className={formLayoutStyles.formActionsCardBody}>
            <div className={formLayoutStyles.formFooterBar}>
              <div className={formLayoutStyles.footerInner}>
                <div className={formLayoutStyles.footerLeft}>
                  <NeonButton
                    id="view-course-delete"
                    dataType={NeonButtonTypes.Tertiary}
                    dataText={VIEW_COURSE_PAGE_TEXT.DeleteCourse}
                    disabled={isCourseLoading}
                    onClick={onDelete}
                    className={styles.deleteCourseBtn}
                  />
                </div>
                <div className={formLayoutStyles.footerButtons}>
                  <NeonButtonFooter
                    id="view-course-form-footer"
                    buttons={[
                      {
                        id: 'view-course-previous',
                        text: VIEW_COURSE_PAGE_TEXT.Previous,
                        type: NeonButtonTypes.Secondary,
                        onClick: onPrevious,
                      },
                    ]}
                    primaryButton={{
                      id: 'view-course-edit',
                      text: VIEW_COURSE_PAGE_TEXT.EditCourse,
                      type: NeonButtonTypes.Secondary,
                      disabled: isCourseLoading,
                      onClick: onEdit,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </NeonCardStandard>
      );
    } else {
      setFooterNode(
        <NeonCardStandard
          className={formLayoutStyles.formActionsCard}
          dataInjectionSpacing="false"
          style={{ borderRadius: 0 }}
        >
          <div data-slot={DATA_SLOT} className={formLayoutStyles.formActionsCardBody}>
            <div className={formLayoutStyles.formFooterBar}>
              <div className={formLayoutStyles.footerInner}>
                <div className={formLayoutStyles.footerButtons}>
                  <NeonButton
                    id="view-course-previous-school"
                    dataType={NeonButtonTypes.Secondary}
                    dataText={VIEW_COURSE_PAGE_TEXT.Previous}
                    onClick={onPrevious}
                  />
                </div>
              </div>
            </div>
          </div>
        </NeonCardStandard>
      );
    }
    return () => setFooterNode(null);
  }, [isDistrictLevel, isCourseLoading, onPrevious, onEdit, onDelete, setFooterNode]);

  return null;
}

const studentViewBottomBarStyle: CSSProperties = {
  ...bottomBarStyle,
  justifyContent: 'flex-end',
};

function ViewCourseStudentCloseBar({ onClose }: { onClose: () => void }): ReactElement {
  return (
    <div style={studentViewBottomBarStyle}>
      <NeonButton
        id="view-course-student-close"
        dataType={NeonButtonTypes.Secondary}
        dataText={VIEW_COURSE_PAGE_TEXT.Close}
        onClick={onClose}
      />
    </div>
  );
}

export type ViewCoursePageProps = {
  /** When true (student catalog route), hide district "Map course to schools". */
  student?: boolean;
};

export function ViewCoursePage({ student = false }: ViewCoursePageProps): ReactElement {
  const navigate = useNavigate();
  const { id: routeCourseId } = useParams<{ id: string }>();
  const courseId = routeCourseId ? decodeURIComponent(routeCourseId) : '';
  const { showToast } = useToastNotifications();
  const location = useLocation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(
    !student && (location.state as { openMapping?: boolean } | null)?.openMapping === true
  );

  const isDistrictLevel = !isCourseCatalogReadOnly();
  const {
    data: course,
    isLoading,
    isError,
    error,
    refetch: refetchCourse,
  } = useGetCourse(courseId, courseId.length > 0);
  /** Staff catalog uses `/highschools`; student host uses district school list for nid→name (NAVI). */
  const { data: staffSchoolsData, isLoading: isStaffSchoolsLoading } = useGetSchools(!student);
  const { data: studentSchoolsData, isLoading: isStudentSchoolsLoading } =
    useGetStudentSchools(student);
  const schoolsData = student ? studentSchoolsData : staffSchoolsData;
  const isSchoolsLoading = student ? isStudentSchoolsLoading : isStaffSchoolsLoading;
  const deleteCourse = useDeleteCourse();

  const handleCatalog = useCallback(() => {
    navigate(`/${STAFF_ROUTES.courseCatalog.list}`);
  }, [navigate]);

  const handleStudentClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleEdit = useCallback(() => {
    navigate(`/${STAFF_ROUTES.courseCatalog.edit(courseId)}`);
  }, [navigate, courseId]);

  const handleDeleteConfirm = useCallback(() => {
    if (!course) {
      return;
    }
    deleteCourse.mutate(course.id, {
      onSuccess: () => {
        showToast('success', {
          heading: formatCourseDeletedSuccessHeading(course.name),
        });
        setShowDeleteModal(false);
        handleCatalog();
      },
      onError: () => {
        showToast('error', { heading: DELETE_COURSE_TOAST.Error });
      },
    });
  }, [course, deleteCourse, showToast, handleCatalog]);

  const filteredSchoolNids = useMemo(() => {
    if (!course?.schools?.length) {
      return [];
    }
    return course.schools.filter((schoolNid) => !DUS_NID_PATTERN.test(schoolNid));
  }, [course?.schools]);

  const mappedSchools = useMemo(() => {
    if (!filteredSchoolNids.length || !schoolsData?.length) {
      return [];
    }
    const byNid = new Map(schoolsData.map((s) => [s.nid, s.name]));
    return filteredSchoolNids
      .map((schoolNid) => ({ nid: schoolNid, label: byNid.get(schoolNid) ?? schoolNid }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredSchoolNids, schoolsData]);

  const attributeLines = useMemo(() => {
    if (!course) {
      return [];
    }
    const lines: string[] = [];
    if (course.repeatable) {
      lines.push(ADD_COURSE_PAGE_TEXT.CanBeRepeatedForCredit);
    }
    if (course.cteCourse) {
      lines.push(ADD_COURSE_PAGE_TEXT.QualifiesAsCTECourse);
    }
    if (course.permissionRequired) {
      lines.push(ADD_COURSE_PAGE_TEXT.RequiresTeacherRecommendation);
    }
    return lines;
  }, [course]);

  const breadcrumbs = useMemo(
    () =>
      getViewCourseBreadcrumbs(course?.name ?? VIEW_COURSE_PAGE_TEXT.BreadcrumbTitleFallback, {
        student,
      }),
    [course?.name, student]
  );

  const handleOpenMapping = useCallback(() => setMappingOpen(true), []);

  const footer = !student ? (
    <ViewCourseStickyFooter
      isDistrictLevel={isDistrictLevel}
      isCourseLoading={isLoading || !course}
      onPrevious={handleCatalog}
      onEdit={handleEdit}
      onDelete={() => setShowDeleteModal(true)}
    />
  ) : null;

  const studentScrollPadStyle: CSSProperties | undefined = student
    ? { paddingBottom: 72 }
    : undefined;

  if (!courseId) {
    return (
      <FormPageShell
        dataTestId="view-course-page"
        breadcrumbs={getViewCourseBreadcrumbs(VIEW_COURSE_PAGE_TEXT.BreadcrumbTitleFallback, {
          student,
        })}
        breadcrumbsId={`${COURSE_CATALOG_ID.Breadcrumbs}-view`}
        pageTitle={VIEW_COURSE_PAGE_TEXT.BreadcrumbTitleFallback}
      >
        <div className={styles.errorBlock} style={studentScrollPadStyle}>
          <NeonErrorPageContent dataErrorCode="404" />
        </div>
        {student ? <ViewCourseStudentCloseBar onClose={handleStudentClose} /> : null}
      </FormPageShell>
    );
  }

  return (
    <FormPageShell
      dataTestId="view-course-page"
      breadcrumbs={breadcrumbs}
      breadcrumbsId={`${COURSE_CATALOG_ID.Breadcrumbs}-view`}
      pageTitle={
        <div className={styles.pageTitleRow}>
          <PageTitle>{course?.name ?? ''}</PageTitle>
          <NeonTag
            id="view-course-type-badge"
            dataText={VIEW_COURSE_PAGE_TEXT.CourseBadgeLabel}
            dataType="default"
          />
        </div>
      }
      pageTitleVariant="plain"
    >
      {footer}

      <div style={studentScrollPadStyle}>
        {isLoading && (
          <div className={formLayoutStyles.formLayoutRoot}>
            <NeonCardStandard
              className={formLayoutStyles.formFieldsCard}
              dataFullHeight="true"
              dataInjectionSpacing="false"
            >
              <div data-slot={DATA_SLOT} className={formLayoutStyles.formFieldsCardBody}>
                <div className={styles.loadingBlock}>
                  <NeonLoadingIndicator />
                </div>
              </div>
            </NeonCardStandard>
          </div>
        )}

        {!isLoading && isError && (
          <div className={styles.errorBlock}>
            <NeonErrorPageContent dataErrorCode="500" />
            {error && (
              <p className={`${styles.value} __neon__text`} role="status">
                {String(error)}
              </p>
            )}
            {!student ? (
              <NeonButton
                id="view-course-back-from-error"
                dataType={NeonButtonTypes.Secondary}
                dataText={VIEW_COURSE_PAGE_TEXT.Previous}
                onClick={handleCatalog}
              />
            ) : null}
          </div>
        )}

        {!isLoading && !isError && course && (
          <div className={formLayoutStyles.formLayoutRoot}>
            <NeonCardStandard
              className={formLayoutStyles.formFieldsCard}
              dataFullHeight="true"
              dataInjectionSpacing="false"
            >
              <div data-slot={DATA_SLOT} className={formLayoutStyles.formFieldsCardBody}>
                <div className={formLayoutStyles.formScrollRegion}>
                  <div className={formLayoutStyles.formContentInner}>
                    <div className={formLayoutStyles.form}>
                      <section
                        className={styles.section}
                        aria-labelledby="view-course-overview-heading"
                      >
                        <h2 id="view-course-overview-heading" className={styles.sectionTitle}>
                          {VIEW_COURSE_PAGE_TEXT.SectionOverview}
                        </h2>

                        {(isDistrictLevel || student) && (
                          <div className={styles.field}>
                            <span className={styles.label}>
                              {VIEW_COURSE_PAGE_TEXT.MappedSchools}
                            </span>
                            <div className={styles.mappedSchoolsBlock}>
                              {mappedSchools.length === 0 ? (
                                <div className={styles.mappedSchoolsWarning} role="status">
                                  <NeonIcon
                                    dataIcon="status-warning"
                                    dataSize="small"
                                    className={styles.mappedSchoolsWarningIcon}
                                    aria-hidden="true"
                                  />
                                  <span className={styles.value}>
                                    {VIEW_COURSE_PAGE_TEXT.NoSchoolsMapped}
                                  </span>
                                </div>
                              ) : (
                                <div className={styles.schoolChips}>
                                  {mappedSchools.map(({ nid, label }) => (
                                    <NeonTag
                                      key={nid}
                                      id={`school-chip-${nid}`}
                                      dataText={label}
                                      dataType="default"
                                    />
                                  ))}
                                </div>
                              )}
                              {!student ? (
                                <NeonButton
                                  id="view-course-map-schools-inline"
                                  dataType={NeonButtonTypes.Secondary}
                                  dataText={`+ ${VIEW_COURSE_PAGE_TEXT.MapCourseToSchools}`}
                                  onClick={handleOpenMapping}
                                  disabled={isLoading}
                                />
                              ) : null}
                            </div>
                          </div>
                        )}

                        <div className={styles.fieldRow}>
                          <ReadOnlyField label={ADD_COURSE_PAGE_TEXT.CourseId} value={course.id} />
                          <ReadOnlyField
                            label={ADD_COURSE_PAGE_TEXT.StateId}
                            value={course.stateId}
                          />
                        </div>
                        <ReadOnlyField
                          label={ADD_COURSE_PAGE_TEXT.SubjectArea}
                          value={course.subjectArea?.name}
                        />
                        <div className={styles.fieldRow}>
                          <ReadOnlyField
                            label={ADD_COURSE_PAGE_TEXT.Credits}
                            value={
                              course.credits !== undefined && course.credits !== null
                                ? String(course.credits)
                                : undefined
                            }
                          />
                          <ReadOnlyField
                            label={ADD_COURSE_PAGE_TEXT.GradeLevels}
                            value={
                              course.grades?.length
                                ? [...course.grades].sort((a, b) => a - b).join(', ')
                                : undefined
                            }
                          />
                        </div>
                        <div className={styles.fieldRow}>
                          <div className={styles.field}>
                            <span className={styles.label}>{ADD_COURSE_PAGE_TEXT.Status}</span>
                            <div className={styles.valueRow}>
                              {course.status === CourseStatus.ACTIVE ? (
                                <NeonIcon dataIcon="status-active" dataSize="small" />
                              ) : (
                                <NeonIcon dataIcon="status-inactive" dataSize="small" />
                              )}
                              <span className={styles.value}>
                                {course.status === CourseStatus.ACTIVE ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                          <ReadOnlyField
                            label={VIEW_COURSE_PAGE_TEXT.CourseDuration}
                            value={getCourseDurationDisplayLabel(course.courseDuration)}
                          />
                        </div>
                      </section>

                      <div className={styles.divider} />

                      <section
                        className={styles.section}
                        aria-labelledby="view-course-details-heading"
                      >
                        <h2 id="view-course-details-heading" className={styles.sectionTitle}>
                          {VIEW_COURSE_PAGE_TEXT.SectionDetails}
                        </h2>
                        <div className={styles.field}>
                          <span className={styles.label}>
                            {ADD_COURSE_PAGE_TEXT.CourseDescription}
                          </span>
                          <div
                            className={detailPageStyles.descriptionBlock}
                            data-testid="view-course-description"
                          >
                            {renderCourseDescriptionContent(course.description)}
                          </div>
                        </div>
                        <ReadOnlyField
                          label={ADD_COURSE_PAGE_TEXT.InstructionalLevel}
                          value={course.instructionalLevel}
                        />
                        <ReadOnlyField
                          label={ADD_COURSE_PAGE_TEXT.CreditType}
                          value={course.creditType}
                        />
                        <div className={styles.field}>
                          <span className={styles.label}>
                            {ADD_COURSE_PAGE_TEXT.CourseAttributes}
                          </span>
                          {attributeLines.length === 0 ? (
                            <span className={styles.value}>{VIEW_COURSE_PAGE_TEXT.EmDash}</span>
                          ) : (
                            <ul className={styles.attributesList}>
                              {attributeLines.map((line) => (
                                <li key={line} className={styles.value}>
                                  {line}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </section>

                      <div className={styles.divider} />

                      <section
                        className={styles.section}
                        aria-labelledby="view-course-relationships-heading"
                      >
                        <h2 id="view-course-relationships-heading" className={styles.sectionTitle}>
                          {VIEW_COURSE_PAGE_TEXT.SectionCourseRelationships}
                        </h2>
                        <CourseRelationshipField
                          label={ADD_COURSE_PAGE_TEXT.CreditRecovery}
                          helperText={
                            student
                              ? VIEW_COURSE_PAGE_TEXT.CreditRecoveryStudentHelper
                              : ADD_COURSE_PAGE_TEXT.CreditRecoveryHelper
                          }
                          fieldKey="credit-recovery"
                          groups={toRelationshipGroups(course.courseCreditRecovery)}
                        />
                        <CourseRelationshipField
                          label={ADD_COURSE_PAGE_TEXT.EquivalentCourses}
                          helperText={
                            student
                              ? VIEW_COURSE_PAGE_TEXT.EquivalentCoursesStudentHelper
                              : ADD_COURSE_PAGE_TEXT.EquivalentHelper
                          }
                          fieldKey="equivalent"
                          groups={toRelationshipGroups(course.equivalentCourses)}
                        />
                        <CourseRelationshipField
                          label={ADD_COURSE_PAGE_TEXT.CoursePrerequisites}
                          helperText={
                            student
                              ? VIEW_COURSE_PAGE_TEXT.CoursePrerequisitesStudentHelper
                              : ADD_COURSE_PAGE_TEXT.PrerequisiteHelper
                          }
                          fieldKey="prerequisites"
                          groups={toRelationshipGroups(course.coursePrerequisites)}
                        />
                        <ReadOnlyField
                          label={ADD_COURSE_PAGE_TEXT.PrerequisiteText}
                          value={course.prerequisites}
                          helperText={
                            student
                              ? VIEW_COURSE_PAGE_TEXT.PrerequisiteDescriptionStudentHelper
                              : undefined
                          }
                        />
                        <CourseRelationshipField
                          label={ADD_COURSE_PAGE_TEXT.CourseCorequisites}
                          helperText={VIEW_COURSE_PAGE_TEXT.CoRequisiteHelper}
                          fieldKey="corequisites"
                          groups={toRelationshipGroups(course.courseCorequisites)}
                        />
                      </section>
                    </div>
                  </div>
                </div>
              </div>
            </NeonCardStandard>
          </div>
        )}

        {course && (
          <DeleteCourseModal
            isOpen={showDeleteModal}
            courseName={course.name}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteConfirm}
            isDeleting={deleteCourse.isPending}
          />
        )}

        {mappingOpen && course && !student && (
          <CourseSchoolsMappingDrawer
            courseId={course.id}
            courseName={course.name}
            currentSchoolNids={filteredSchoolNids}
            allSchools={schoolsData ?? []}
            isSchoolsLoading={isSchoolsLoading}
            defaultRadio="select"
            onClose={() => setMappingOpen(false)}
            onSaveSuccess={() => {
              setMappingOpen(false);
              void refetchCourse();
            }}
          />
        )}
      </div>

      {student ? <ViewCourseStudentCloseBar onClose={handleStudentClose} /> : null}
    </FormPageShell>
  );
}

function ReadOnlyField({
  label,
  value,
  helperText,
}: {
  label: string;
  value: string | undefined;
  helperText?: string;
}): ReactElement {
  const display = value?.trim() ? value : undefined;
  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      {helperText !== undefined && helperText.length > 0 ? (
        <span className={styles.helperText}>{helperText}</span>
      ) : null}
      <span className={styles.value}>{display ?? VIEW_COURSE_PAGE_TEXT.EmDash}</span>
    </div>
  );
}

function CourseRelationshipField({
  label,
  helperText,
  fieldKey,
  groups,
}: {
  label: string;
  helperText: string;
  fieldKey: string;
  groups: Array<Array<{ id: string; name: string }>>;
}): ReactElement {
  const isEmpty = groups.length === 0;
  const [modalGroupIndex, setModalGroupIndex] = useState<number | null>(null);
  const modalGroup = modalGroupIndex !== null ? (groups[modalGroupIndex] ?? []) : [];

  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      <span className={styles.helperText}>{helperText}</span>
      {isEmpty ? (
        <span className={styles.value}>{VIEW_COURSE_PAGE_TEXT.RelationshipEmpty}</span>
      ) : (
        <div className={styles.courseGroups}>
          {groups.map((group, gIdx) => (
            <div key={gIdx} className={styles.courseGroupRow}>
              {gIdx > 0 && (
                <span className={styles.orDivider}>{VIEW_COURSE_PAGE_TEXT.OrDivider}</span>
              )}
              <RelationshipSegment
                fieldKey={fieldKey}
                segmentIndex={gIdx}
                courses={group}
                onOpenModal={() => setModalGroupIndex(gIdx)}
              />
            </div>
          ))}
        </div>
      )}

      {modalGroupIndex !== null && (
        <NeonModalDialog
          id={`rel-overflow-modal-${fieldKey}`}
          dataSize="medium"
          dataCloseOnBackdropClick="true"
          neonDialogHasClosed={() => setModalGroupIndex(null)}
        >
          <div data-slot="dialog-header-title">{label}</div>
          <div data-slot="dialog-body">
            <div className={styles.relationshipOverflowModalBody}>
              {modalGroup.map((course) => (
                <div
                  key={`modal-${fieldKey}-${course.id}`}
                  className={styles.relationshipOverflowRow}
                >
                  <div className={styles.relationshipOverflowCourseName}>{course.name}</div>
                  <div className={styles.relationshipOverflowCourseId}>{course.id}</div>
                </div>
              ))}
            </div>
          </div>
        </NeonModalDialog>
      )}
    </div>
  );
}

function RelationshipSegment({
  fieldKey,
  segmentIndex,
  courses,
  onOpenModal,
}: {
  fieldKey: string;
  segmentIndex: number;
  courses: Array<{ id: string; name: string }>;
  onOpenModal: () => void;
}): ReactElement {
  const [visibleCount, setVisibleCount] = useState<number>(courses.length);
  const measureRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const measureContainerRef = useRef<HTMLDivElement | null>(null);
  const measureCountRef = useRef<HTMLSpanElement | null>(null);
  const renderedContainerRef = useRef<HTMLDivElement | null>(null);

  const recalculateVisibleCount = useCallback(() => {
    if (courses.length === 0) {
      setVisibleCount(0);
      return;
    }

    const container = measureContainerRef.current;
    if (!container) {
      setVisibleCount(courses.length);
      return;
    }

    const containerWidth = container.clientWidth;
    if (containerWidth <= 0) {
      setVisibleCount(courses.length);
      return;
    }

    const chips = measureRefs.current.slice(0, courses.length).filter(Boolean) as HTMLSpanElement[];
    if (chips.length === 0) {
      setVisibleCount(courses.length);
      return;
    }

    const countEl = measureCountRef.current;
    const countWidth = countEl?.offsetWidth ?? 0;
    const gap = parseFloat(window.getComputedStyle(container).columnGap || '8') || 8;
    const chipWidths = chips.map((chip) => chip.offsetWidth);

    const rowsRequired = (visible: number): number => {
      let rows = 1;
      let rowWidth = 0;

      const place = (width: number): void => {
        if (rowWidth === 0) {
          rowWidth = width;
          return;
        }

        const nextWidth = rowWidth + gap + width;
        if (nextWidth <= containerWidth) {
          rowWidth = nextWidth;
        } else {
          rows += 1;
          rowWidth = width;
        }
      };

      for (let i = 0; i < visible; i += 1) {
        place(chipWidths[i]);
      }

      if (visible < chipWidths.length && countWidth > 0) {
        place(countWidth);
      }

      return rows;
    };

    let nextVisible = chipWidths.length;
    while (nextVisible > 1 && rowsRequired(nextVisible) > MAX_RELATIONSHIP_TILE_ROWS) {
      nextVisible -= 1;
    }

    setVisibleCount(Math.max(1, nextVisible));
  }, [courses]);

  useEffect(() => {
    const frame = requestAnimationFrame(recalculateVisibleCount);
    const timeoutA = window.setTimeout(recalculateVisibleCount, 120);
    const timeoutB = window.setTimeout(recalculateVisibleCount, 350);
    window.addEventListener('resize', recalculateVisibleCount);

    const observer = new ResizeObserver(() => {
      recalculateVisibleCount();
    });

    if (measureContainerRef.current) {
      observer.observe(measureContainerRef.current);
    }
    if (renderedContainerRef.current) {
      observer.observe(renderedContainerRef.current);
    }

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timeoutA);
      window.clearTimeout(timeoutB);
      window.removeEventListener('resize', recalculateVisibleCount);
      observer.disconnect();
    };
  }, [recalculateVisibleCount]);

  const hiddenCount = Math.max(0, courses.length - visibleCount);
  const visibleCourses = hiddenCount > 0 ? courses.slice(0, visibleCount) : courses;

  return (
    <div className={styles.courseGroupOverflow}>
      <div className={styles.relationshipMeasureRow} aria-hidden="true">
        <div
          ref={measureContainerRef}
          className={`${styles.courseChips} ${styles.courseChipsOverflow}`}
        >
          {courses.map((course, idx) => (
            <span
              key={`measure-${fieldKey}-${segmentIndex}-${course.id}`}
              className={styles.relationshipChipWrap}
              ref={(el) => {
                measureRefs.current[idx] = el;
              }}
            >
              <NeonTag
                id={`rel-chip-measure-${fieldKey}-${segmentIndex}-${course.id}`}
                dataText={course.name}
                dataSubText={course.id}
                dataType="default"
              />
            </span>
          ))}
          <span
            ref={measureCountRef}
            className={styles.overflowCoursesButtonLink}
          >{`+ ${courses.length} ${courses.length === 1 ? 'course' : 'courses'}`}</span>
        </div>
      </div>

      <div
        ref={renderedContainerRef}
        className={`${styles.courseChips} ${styles.courseChipsOverflow}`}
      >
        {visibleCourses.map((course) => (
          <span
            key={`${fieldKey}-${segmentIndex}-${course.id}`}
            className={styles.relationshipChipWrap}
          >
            <NeonTag
              id={`rel-chip-${fieldKey}-${segmentIndex}-${course.id}`}
              dataText={course.name}
              dataSubText={course.id}
              dataType="default"
            />
          </span>
        ))}

        {hiddenCount > 0 && (
          <button
            id={`rel-overflow-${fieldKey}-${segmentIndex}`}
            type="button"
            className={styles.overflowCoursesButtonLink}
            onClick={onOpenModal}
          >
            {`+ ${hiddenCount} ${hiddenCount === 1 ? 'course' : 'courses'}`}
          </button>
        )}
      </div>
    </div>
  );
}
